/**
 * Form field info icons — hover tooltips from FIELD_HELP
 */

import { FIELD_HELP } from '../constants/FieldHelp.js';

const INFO_SVG = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><circle cx="7" cy="7" r="6" stroke="currentColor" stroke-width="1"/><path d="M7 6.2V10M7 4.2h.01" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>`;

export class FieldHelpManager {
    static _tipEl = null;
    static _activeIcon = null;
    static _hideTimer = null;

    static init() {
        if (FieldHelpManager._bound) return;
        FieldHelpManager._bound = true;

        document.addEventListener('mouseover', (e) => {
            const icon = e.target.closest('.field-help-icon');
            if (!icon) return;
            FieldHelpManager.clearHideTimer();
            FieldHelpManager.show(icon);
        }, true);

        document.addEventListener('mouseout', (e) => {
            const icon = e.target.closest('.field-help-icon');
            if (!icon) return;
            const related = e.relatedTarget;
            if (related && (icon.contains(related) || FieldHelpManager._tipEl?.contains(related))) return;
            FieldHelpManager.scheduleHide();
        }, true);

        document.addEventListener('scroll', () => FieldHelpManager.hide(), true);
        window.addEventListener('blur', () => FieldHelpManager.hide());
    }

    static clearHideTimer() {
        if (FieldHelpManager._hideTimer) {
            clearTimeout(FieldHelpManager._hideTimer);
            FieldHelpManager._hideTimer = null;
        }
    }

    static scheduleHide() {
        FieldHelpManager.clearHideTimer();
        FieldHelpManager._hideTimer = setTimeout(() => FieldHelpManager.hide(), 100);
    }

    static show(icon) {
        const title = icon.getAttribute('data-help-title') || '';
        const body = icon.getAttribute('data-help-body') || '';
        if (!body) return;
        if (FieldHelpManager._activeIcon === icon) return;
        FieldHelpManager.hide();

        const tip = document.createElement('div');
        tip.className = 'rich-tooltip field-help-tooltip';
        tip.id = 'activeFieldHelpTooltip';
        tip.setAttribute('role', 'tooltip');
        tip.innerHTML = `
            ${title ? `<div class="rich-tooltip-title">${title}</div>` : ''}
            <div class="rich-tooltip-body">${body}</div>
        `;
        document.body.appendChild(tip);
        FieldHelpManager._tipEl = tip;
        FieldHelpManager._activeIcon = icon;

        const rect = icon.getBoundingClientRect();
        const tipRect = tip.getBoundingClientRect();
        let left = rect.left + rect.width / 2 - tipRect.width / 2;
        let top = rect.top - tipRect.height - 8;
        if (left < 8) left = 8;
        if (left + tipRect.width > window.innerWidth - 8) {
            left = window.innerWidth - tipRect.width - 8;
        }
        if (top < 8) {
            top = rect.bottom + 8;
            tip.classList.add('rich-tooltip-below');
        }
        tip.style.left = `${left}px`;
        tip.style.top = `${top}px`;
    }

    static hide() {
        FieldHelpManager._tipEl?.remove();
        FieldHelpManager._tipEl = null;
        FieldHelpManager._activeIcon = null;
    }

    static resolveHelp(id) {
        if (!id) return null;
        if (FIELD_HELP[id]) return FIELD_HELP[id];
        const aliases = {
            dealProbabilitySlider: 'dealProbability',
            dealProbabilityValue: 'dealProbability',
            dealContractValue: 'dealContractValue',
            dealAmount: 'dealAmount',
            dealCommissionEligible: 'dealCommissionEligible',
            dealCommissionRate: 'dealCommissionRate',
            dealCommissionFixed: 'dealCommissionFixed',
            dealCommissionModel: 'dealCommissionModel',
            dealTaxEnabled: 'dealInvoiceAmount',
            dealTaxRate: 'dealInvoiceAmount',
        };
        const mapped = aliases[id];
        if (mapped && FIELD_HELP[mapped]) return FIELD_HELP[mapped];
        const base = id.replace(/Slider$|Value$|Group$/, '');
        if (FIELD_HELP[base]) return FIELD_HELP[base];
        return null;
    }

    static enhanceForm(root) {
        if (!root) return;
        root.querySelectorAll('label[for]').forEach(label => {
            const id = label.getAttribute('for');
            const help = FieldHelpManager.resolveHelp(id);
            if (!help || label.querySelector('.field-help-icon')) return;

            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'field-help-icon';
            btn.setAttribute('aria-label', `Help: ${help.title || id}`);
            btn.setAttribute('data-help-title', help.title || '');
            btn.setAttribute('data-help-body', help.body);
            btn.innerHTML = INFO_SVG;
            label.appendChild(btn);
        });
        root.querySelectorAll('label:not([for])').forEach(label => {
            const text = (label.textContent || '').trim().toLowerCase();
            let key = null;
            if (text.includes('commission eligible')) key = 'dealCommissionEligible';
            if (text.includes('divide amount equally')) key = 'dealPaymentModel';
            if (!key) return;
            const help = FIELD_HELP[key];
            if (!help || label.querySelector('.field-help-icon')) return;
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'field-help-icon';
            btn.setAttribute('aria-label', `Help: ${help.title}`);
            btn.setAttribute('data-help-title', help.title);
            btn.setAttribute('data-help-body', help.body);
            btn.innerHTML = INFO_SVG;
            label.appendChild(btn);
        });
    }

    static enhanceAllForms() {
        document.querySelectorAll('.deal-form, .account-modal-form, .entity-modal-form').forEach(form => {
            FieldHelpManager.enhanceForm(form);
        });
    }
}
